import { hbs } from 'ember-cli-htmlbars';
const __COLOCATED_TEMPLATE__ = hbs("{{! @glint-nocheck: not typesafe yet }}\n{{#if this.isVisible}}\n  <Shared::Modal @closable={{false}} {{did-insert this.loadFacebookSDK}} as |modal|>\n    <modal.card @portrait={{true}}>\n      <modal.card-header>\n        <h2 class=\"o--modalHeader__title\">\n          {{t \"instagram_explore.auth.reauthenticate\" profileType=this.socialProfile.accountType}}\n        </h2>\n      </modal.card-header>\n\n      <modal.card-body>\n        <Shared::ProfileAvatar @size=\"large\" @socialProfile={{this.socialProfile}}>\n          <span class=\"o--user__desc\">\n            {{t \"instagram_explore.auth.reconnect\" name=this.socialProfile.accountType}}\n          </span>\n          <span class=\"o--user__title\">\n            {{this.socialProfile.displayName}}\n          </span>\n        </Shared::ProfileAvatar>\n\n        <hr />\n\n        <p>\n          {{t\n            \"instagram_explore.auth.refresh_profile\"\n            nickname=this.socialProfile.displayNickname\n            profileType=this.socialProfile.accountType\n          }}\n        </p>\n      </modal.card-body>\n\n      <modal.card-footer>\n        <Shared::Ui::Button @onClick={{this.dismiss}} @text={{t \"instagram_explore.auth.later\"}} @variant=\"outline\" />\n\n        <Shared::Ui::Button\n          @onClick={{this.reconnectSocialProfile}}\n          @text={{t \"instagram_explore.auth.reconnect\" name=this.socialProfile.displayNickname}}\n          @variant=\"primary\"\n          class=\"u--float--right\"\n        />\n      </modal.card-footer>\n    </modal.card>\n  </Shared::Modal>\n{{/if}}", {"contents":"{{! @glint-nocheck: not typesafe yet }}\n{{#if this.isVisible}}\n  <Shared::Modal @closable={{false}} {{did-insert this.loadFacebookSDK}} as |modal|>\n    <modal.card @portrait={{true}}>\n      <modal.card-header>\n        <h2 class=\"o--modalHeader__title\">\n          {{t \"instagram_explore.auth.reauthenticate\" profileType=this.socialProfile.accountType}}\n        </h2>\n      </modal.card-header>\n\n      <modal.card-body>\n        <Shared::ProfileAvatar @size=\"large\" @socialProfile={{this.socialProfile}}>\n          <span class=\"o--user__desc\">\n            {{t \"instagram_explore.auth.reconnect\" name=this.socialProfile.accountType}}\n          </span>\n          <span class=\"o--user__title\">\n            {{this.socialProfile.displayName}}\n          </span>\n        </Shared::ProfileAvatar>\n\n        <hr />\n\n        <p>\n          {{t\n            \"instagram_explore.auth.refresh_profile\"\n            nickname=this.socialProfile.displayNickname\n            profileType=this.socialProfile.accountType\n          }}\n        </p>\n      </modal.card-body>\n\n      <modal.card-footer>\n        <Shared::Ui::Button @onClick={{this.dismiss}} @text={{t \"instagram_explore.auth.later\"}} @variant=\"outline\" />\n\n        <Shared::Ui::Button\n          @onClick={{this.reconnectSocialProfile}}\n          @text={{t \"instagram_explore.auth.reconnect\" name=this.socialProfile.displayNickname}}\n          @variant=\"primary\"\n          class=\"u--float--right\"\n        />\n      </modal.card-footer>\n    </modal.card>\n  </Shared::Modal>\n{{/if}}","moduleName":"later/components/reconnect-social-profile-modal.hbs","parseOptions":{"srcName":"later/components/reconnect-social-profile-modal.hbs"}});
import { assert } from '@ember/debug';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isNone } from '@ember/utils';
import Component from '@glimmer/component';

import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
import type SocialProfileModel from 'later/models/social-profile';
import type AlertsService from 'later/services/alerts';
import type AuthService from 'later/services/auth';
import type CredentialStatusService from 'later/services/credential-status';
import type ErrorsService from 'later/services/errors';
import type InstagramService from 'later/services/instagram';
import type JWTService from 'later/services/jwt';
import type PinterestService from 'later/services/pinterest';
import type TwitterService from 'later/services/twitter';
import type { UntypedService } from 'shared/types';

interface ReconnectSocialProfileModalComponentArgs {
  onSuccess: () => void;
}

interface FacebookResponse {
  accessToken: string;
}

interface FacebookPage {
  id: string;
  access_token: string;
}

interface PinterestResponse {
  credentials: {
    token: string;
  };
}

export default class ReconnectSocialProfileModalComponent extends Component<ReconnectSocialProfileModalComponentArgs> {
  @service declare auth: AuthService;
  @service declare alerts: AlertsService;
  @service declare errors: ErrorsService;
  @service('social/facebook-auth') declare facebookAuth: UntypedService;
  @service declare intl: IntlService;
  @service declare instagram: InstagramService;
  @service declare instagramBusiness: UntypedService;
  @service declare jwt: JWTService;
  @service declare pinterest: PinterestService;
  @service declare router: RouterService;
  @service declare twitter: TwitterService;
  @service declare credentialStatus: CredentialStatusService;

  get socialProfile(): SocialProfileModel {
    assert('A SocialProfile must be set', this.credentialStatus.socialProfileToReconnect);
    return this.credentialStatus.socialProfileToReconnect;
  }

  get isVisible(): boolean {
    return this.credentialStatus.isReconnectSocialProfileModalVisible;
  }

  get facebookProfiles(): SocialProfileModel[] {
    return this.auth.socialProfiles.filter((profile) => profile.isFacebook);
  }

  @action
  loadFacebookSDK(): void {
    this.facebookAuth.loadFacebookSDK();
  }

  @action
  reconnectSocialProfile(): void {
    if (this.socialProfile.isInstagram) {
      this.reconnectInstagram();
    } else if (this.socialProfile.isTwitter) {
      this.twitter.loginTwitter();
    } else if (this.socialProfile.isFacebook) {
      this.reconnectFacebook();
    } else if (this.socialProfile.isPinterest) {
      this.reconnectPinterest();
    } else {
      this.errors.log(new Error('Unknown social profile type'), {
        socialProfileId: this.socialProfile?.id,
        socialProfileType: this.socialProfile?.type || ''
      });
    }
  }

  async reconnectInstagram(): Promise<void> {
    this.#saveAndClose();

    const isRefresh = true;
    const {
      uid,
      credentials: { token },
      scope: permissionScope
    } = await this.instagram.loginInstagram(this.socialProfile, isRefresh);

    const username = await this.instagram.fetchBasicDisplayUsername(token);
    if (uid === this.socialProfile.uid || username === this.socialProfile.nickname) {
      const scopes = isNone(this.socialProfile.permissionScope) ? [] : this.socialProfile.permissionScope;
      const uniqueScopes = [...new Set([...scopes, ...permissionScope])];

      this.socialProfile.basicDisplayId = uid;
      this.socialProfile.basicDisplayToken = token;
      this.socialProfile.permissionScope = uniqueScopes;

      this.#saveAndClose();
    } else {
      this.alerts.info(
        this.intl.t('alerts.account.controllers.groups.wrong_profile', {
          social_profile: this.socialProfile.nickname,
          username
        })
      );
    }
  }

  async reconnectFacebook(): Promise<void> {
    try {
      const { accessToken }: FacebookResponse = await this.facebookAuth.authenticateFacebook();

      const { facebookProfiles } = this;
      FB.api('/me?fields=picture,name', ({ id }: { id: string }) => {
        const facebookProfile = facebookProfiles.find((profile) => profile.uid === id);

        if (!isNone(facebookProfile)) {
          this.instagramBusiness.updateSocialProfile.perform(facebookProfile, accessToken);
        }

        let foundProfile = id === this.socialProfile.uid;

        //try the pages user has access to, update them all
        FB.api('/me/accounts?fields=picture,name,access_token,tasks,category', ({ data: pages }) => {
          pages.forEach((page: FacebookPage) => {
            const facebookPage = facebookProfiles.find((profile) => profile.uid === page.id);
            if (facebookPage) {
              if (page.id === this.socialProfile.uid) {
                foundProfile = true;
              }
              this.instagramBusiness.updateSocialProfile.perform(facebookPage, page.access_token);
            }
          });

          if (!foundProfile) {
            this.alerts.info(this.intl.t('alerts.account.controllers.groups.reconnect_error'));
          }

          this.#saveAndClose();
        });
      });
    } catch (error) {
      this.alerts.info(this.intl.t('alerts.account.controllers.groups.facebook.login_error'));
    }
  }

  async reconnectPinterest(): Promise<void> {
    // This functionality may not work as expect as
    // the `loginPinterest`method does not return anything.
    const {
      credentials: { token }
    } = this.pinterest.loginPinterest() as unknown as PinterestResponse;

    if (!token) {
      this.alerts.info(this.intl.t('alerts.account.controllers.groups.reconnect_error'));
      return;
    }

    this.socialProfile.token = token;

    await this.#saveAndClose();
  }

  @action
  dismiss(): void {
    this.router.transitionTo('cluster.schedule');
    this.credentialStatus.closeReconnectSocialProfileModal();
    return;
  }

  async #saveAndClose(): Promise<void> {
    this.jwt.clearToken();
    await this.socialProfile.save();

    this.credentialStatus.closeReconnectSocialProfileModal();
    this.alerts.success(this.intl.t('alerts.account.controllers.groups.reconnect_success'));

    this.args.onSuccess();
  }
}
